import {
  type DocumentType,
  type ErrorType,
  getApiDocumentsV1DocumentsRetrieveQueryKey,
  useApiDocumentsV1DocumentsPartialUpdate,
  useApiDocumentsV1DocumentsUploadCreate,
  useQueryClient,
  useUploadFile,
} from 'api';
import { useNotification } from 'features';
import { translate } from 'i18n';

export const useUploadDocument = (documentId: number) => {
  const queryClient = useQueryClient();
  const { open } = useNotification();

  const { mutateAsync: generatePreSignedURL, isPending: isGeneratingUrl } =
    useApiDocumentsV1DocumentsUploadCreate<ErrorType<Error>>();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const { mutateAsync: saveDocument, isPending: isSaving } =
    useApiDocumentsV1DocumentsPartialUpdate<ErrorType<Error>>();

  const upload = async (file: File): Promise<string> => {
    try {
      const { upload_url, file_path } = await generatePreSignedURL({
        data: { content_type: file.type },
        id: documentId,
      });

      await uploadFile({
        file,
        headers: { 'content-type': file.type },
        url: upload_url,
      });

      return file_path;
    } catch (error) {
      open({
        message: translate('backlog.documentGeneration.notifications.failedToUploadFile', {
          namespace: 'features',
        }),
        type: 'error',
      });
      throw error;
    }
  };

  const save = async (filePath: string, documentType: DocumentType) => {
    try {
      await saveDocument({
        data: {
          document_type: documentType,
          file_path: filePath,
        },
        id: documentId,
      });
      queryClient.invalidateQueries({
        queryKey: getApiDocumentsV1DocumentsRetrieveQueryKey(documentId),
      });
    } catch (error) {
      open({
        message: translate('backlog.documentGeneration.notifications.failedToSaveFile', {
          namespace: 'features',
        }),
        type: 'error',
      });
      throw error;
    }
  };

  return {
    isSaving: isSaving,
    isUploading: isGeneratingUrl || isUploading,
    save,
    upload,
  };
};
